import React, { ReactElement, useEffect, useRef, useState } from "react";

type CelebrationProps = {
  /** Firework colours: the winning team's. */
  colors: string[];
  /** How long new fireworks keep launching, in seconds. */
  seconds?: number;
};

type Particle = {
  x: number;
  y: number;
  /** Where it was a frame ago, so each spark is drawn as a short line instead of a dot. */
  px: number;
  py: number;
  vx: number;
  vy: number;
  age: number;
  life: number;
  size: number;
  color: string;
  /** A rocket climbs, leaves a trail and bursts at its target height. */
  rocket?: { targetY: number; colors: string[] };
};

const GRAVITY = 90; // px / s^2
const rand = (a: number, b: number) => a + Math.random() * (b - a);

/**
 * A few seconds of fireworks in the winning team's colours, drawn over the game-over screen.
 * It ignores the mouse, removes itself when the last spark fades, and does nothing at all for players who
 * ask their device to reduce motion.
 */
export default function Celebration({ colors, seconds = 5 }: CelebrationProps): ReactElement | null {
  const canvas = useRef<HTMLCanvasElement>(null);
  const [done, setDone] = useState(false);

  useEffect(() => {
    const el = canvas.current;
    const ctx = el ? el.getContext("2d") : null;
    if (!el || !ctx || (window.matchMedia && window.matchMedia("(prefers-reduced-motion: reduce)").matches)) {
      setDone(true);
      return;
    }

    const dpr = Math.min(window.devicePixelRatio || 1, 2);
    let width = window.innerWidth;
    let height = window.innerHeight;
    const resize = () => {
      width = window.innerWidth;
      height = window.innerHeight;
      el.width = width * dpr;
      el.height = height * dpr;
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    };
    resize();
    window.addEventListener("resize", resize);

    let particles: Particle[] = [];
    let elapsed = 0;
    let nextLaunch = 0.15;
    let frame = 0;
    let last = performance.now();

    const launch = () => {
      const pick = () => colors[Math.floor(Math.random() * colors.length)];
      particles.push({
        x: rand(width * 0.15, width * 0.85),
        y: height + 10,
        px: 0,
        py: height + 10,
        vx: rand(-40, 40),
        vy: -rand(height * 0.75, height * 1.0),
        age: 0,
        life: 3,
        size: 2.4,
        color: "#fff4d6",
        rocket: { targetY: rand(height * 0.12, height * 0.42), colors: [pick(), pick()] },
      });
    };

    const burst = (x: number, y: number, palette: string[]) => {
      const count = Math.round(rand(90, 140));
      const big = Math.random() < 0.35;
      for (let i = 0; i < count; i++) {
        const angle = (Math.PI * 2 * i) / count + rand(-0.05, 0.05);
        const speed = rand(60, big ? 330 : 240);
        particles.push({
          x,
          y,
          px: x,
          py: y,
          vx: Math.cos(angle) * speed,
          vy: Math.sin(angle) * speed,
          age: 0,
          life: rand(1.3, 2.2),
          size: rand(2.2, 3.8),
          color: palette[i % palette.length],
        });
      }
    };

    const tick = (now: number) => {
      const dt = Math.min(0.05, (now - last) / 1000);
      last = now;
      elapsed += dt;

      if (elapsed < seconds && elapsed >= nextLaunch) {
        launch();
        if (Math.random() < 0.4) launch();
        nextLaunch = elapsed + rand(0.25, 0.55);
      }

      // Fade the previous frame instead of clearing it, which leaves short trails behind each spark.
      ctx.globalCompositeOperation = "destination-out";
      ctx.fillStyle = "rgba(0, 0, 0, 0.22)";
      ctx.fillRect(0, 0, width, height);
      ctx.globalCompositeOperation = "lighter";

      const alive: Particle[] = [];
      for (const p of particles) {
        p.age += dt;
        p.px = p.x;
        p.py = p.y;
        p.x += p.vx * dt;
        p.y += p.vy * dt;
        if (p.rocket) {
          p.vy += GRAVITY * 0.6 * dt;
          if (p.y <= p.rocket.targetY || p.vy >= -40) {
            burst(p.x, p.y, p.rocket.colors);
            continue;
          }
        } else {
          p.vx *= 1 - 1.4 * dt;
          p.vy = p.vy * (1 - 1.4 * dt) + GRAVITY * dt;
          if (p.age >= p.life) continue;
        }
        const fade = p.rocket ? 1 : Math.max(0, 1 - p.age / p.life);
        ctx.globalAlpha = fade;
        ctx.strokeStyle = p.color;
        ctx.lineCap = "round";
        ctx.lineWidth = p.size * (p.rocket ? 1 : 0.6 + 0.4 * fade) * 1.4;
        ctx.beginPath();
        ctx.moveTo(p.px, p.py);
        ctx.lineTo(p.x, p.y);
        ctx.stroke();
        alive.push(p);
      }
      ctx.globalAlpha = 1;
      particles = alive;

      if (elapsed >= seconds && particles.length === 0) {
        setDone(true);
        return;
      }
      frame = requestAnimationFrame(tick);
    };
    frame = requestAnimationFrame(tick);

    return () => {
      cancelAnimationFrame(frame);
      window.removeEventListener("resize", resize);
    };
    // The colours of a finished game never change.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (done) return null;
  return <canvas ref={canvas} className="go-fireworks" aria-hidden="true" />;
}
